import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import {
  Facebook,
  Linkedin,
  TwitterX,
  Whatsapp
} from "@yleisradio/yds-icons-react";
import styled, { css } from "styled-components";

import { useEmbedParametersContext } from "../../../contexts/embedParametersContext";
import { getAreenaUrl } from "../../../helpers/areenaLinks";
import { localizedText } from "../../../helpers/previewHelpers";
import { useThemeColors } from "../../../hooks/useThemeColors/useThemeColors";
import CopyIcon from "../../../icons/CopyIcon";
import EmailIcon from "../../../icons/EmailIcon";
import ShareIcon from "../../../icons/ShareIcon";
import i18n from "../../../service/i18n";
import { usePlayerStore } from "../../../store/usePlayerStore";
import { LocalizedText } from "../../../types/player-api";
import IconButton from "../IconButton";

import ShareItem from "./ShareItem/ShareItem";

const transitionDelay = 400;

const Menu = styled.nav<{
  backgroundColor: string;
  isActive: boolean;
  menuPosition: CSSProperties;
  textColor: string;
}>`
  background-color: ${props => props.backgroundColor};
  border-radius: 9px;
  box-shadow: rgb(0 0 0 / 30%) 0 1px 8px;
  box-sizing: border-box;
  color: ${props => props.textColor};
  opacity: 0;
  right: ${props => props.menuPosition.right};
  top: ${props => props.menuPosition.top};
  bottom: ${props => props.menuPosition.bottom};
  position: absolute;
  transform: translateY(-20px);
  transition:
    opacity ${transitionDelay}ms ease,
    transform ${transitionDelay}ms ease;
  z-index: 1000;

  ${(props: { isActive: boolean }) =>
    props.isActive &&
    css`
      opacity: 1;
      transform: translateY(0);
    `}
`;

const MenuList = styled.ul`
  box-sizing: border-box;
  gap: 1rem;
  list-style-type: none;
  margin: 0;
  padding: 0;
  width: 10.75rem;
`;

const ShareWrapper = styled.div`
  position: relative;
`;

const getCssPosition = (
  wrapperElement: HTMLDivElement,
  menuListElement: HTMLUListElement
): CSSProperties => {
  const position: CSSProperties = {};
  const shareListHeight = menuListElement.getBoundingClientRect().height;
  const windowHeight = window.innerHeight;
  const { bottom: buttonBottom, width: buttonWidth } =
    wrapperElement.getBoundingClientRect();

  position.right = `${buttonWidth + 8}px`;

  if (windowHeight < buttonBottom + shareListHeight) {
    position.top = `${-shareListHeight}px`;
  } else {
    position.bottom = `${-shareListHeight}px`;
  }

  return position;
};

const Share = ({
  audioTitle,
  fillColor,
  language
}: {
  audioTitle?: LocalizedText;
  fillColor: string;
  language: string;
}) => {
  const { useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const isSimulcast = useStoreState.isSimulcast();
  const programId = useStoreState.programId();

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [menuTransition, setMenuTransition] = useState<boolean>(false);
  const [positionProps, setPositionProps] = useState<CSSProperties>({
    right: 0
  });

  const shareAudioLabel = i18n("share_audio", { language });

  //Refs
  const copyLinkClicked = useRef<boolean>(false);
  const menuListRef = useRef<HTMLUListElement>(null);
  const menuOpenRef = useRef<boolean>();
  const shareWrapperRef = useRef<HTMLDivElement>(null);
  menuOpenRef.current = menuOpen;

  const {
    backgroundInteractive,
    defaultTextColor,
    shareLinkHoverBackgroundColor
  } = useThemeColors(useEmbedParametersContext().theme);

  const focusEventHandler = useCallback(
    event => {
      if (
        menuOpenRef.current &&
        shareWrapperRef.current &&
        (!shareWrapperRef.current.contains(event.target) ||
          copyLinkClicked.current)
      ) {
        copyLinkClicked.current = false;
        setMenuTransition(false);
        setTimeout(() => {
          setMenuOpen(false);
        }, transitionDelay);
      }
    },
    [copyLinkClicked]
  );

  useEffect(() => {
    document.addEventListener("click", focusEventHandler);
    document.addEventListener("focusin", focusEventHandler);
    return () => {
      document.removeEventListener("click", focusEventHandler);
      document.removeEventListener("focusin", focusEventHandler);
      setMenuOpen(false);
    };
  }, [focusEventHandler]);

  const handleClick = useCallback(() => {
    if (!programId) return;
    const data = {
      title: localizedText(audioTitle, language),
      url: getAreenaUrl({ isSimulcast, language, programId })
    };

    const useNativeDialog =
      device.isMobile && navigator.canShare && navigator.canShare(data);

    if (useNativeDialog) {
      navigator.share(data);
    } else {
      setTimeout(() => {
        if (shareWrapperRef.current && menuListRef.current) {
          setPositionProps(
            getCssPosition(shareWrapperRef.current, menuListRef.current)
          );
        }
      });

      if (menuOpenRef.current) {
        setMenuTransition(false);
        setTimeout(() => {
          setMenuOpen(false);
        }, transitionDelay);
      } else {
        setMenuOpen(true);
        setTimeout(() => {
          setMenuTransition(true);
        });
      }
    }
  }, [device, audioTitle, language, isSimulcast, programId]);

  return (
    <ShareWrapper ref={shareWrapperRef}>
      <IconButton
        aria-expanded={menuOpen}
        aria-haspopup="true"
        aria-label={shareAudioLabel}
        isMobile={device.isMobile}
        onClick={handleClick}>
        <ShareIcon fillColor={fillColor} />
      </IconButton>

      {menuOpen && (
        <Menu
          backgroundColor={backgroundInteractive}
          data-testid="share-menu"
          isActive={menuTransition}
          menuPosition={positionProps}
          textColor={defaultTextColor}>
          <MenuList ref={menuListRef}>
            <ShareItem
              copyLinkClicked={copyLinkClicked}
              copyUrl
              hoverBackgroundColor={shareLinkHoverBackgroundColor}
              icon={<CopyIcon fillColor={defaultTextColor} />}
              language={language}
              source="copy-link"
              title={i18n("copy_link", { language })}
            />

            <ShareItem
              hoverBackgroundColor={shareLinkHoverBackgroundColor}
              icon={<EmailIcon fillColor={defaultTextColor} />}
              language={language}
              source="email"
              title={i18n("email", { language })}
              url={`mailto:?subject=${localizedText(
                audioTitle,
                language
              )}&body=`}
            />

            <ShareItem
              hoverBackgroundColor={shareLinkHoverBackgroundColor}
              icon={<Whatsapp color={defaultTextColor} />}
              language={language}
              title="WhatsApp"
              url="https://wa.me?text="
            />

            <ShareItem
              hoverBackgroundColor={shareLinkHoverBackgroundColor}
              icon={<Facebook color={defaultTextColor} />}
              language={language}
              title="Facebook"
              url="https://www.facebook.com/sharer/sharer.php?u="
            />

            <ShareItem
              hoverBackgroundColor={shareLinkHoverBackgroundColor}
              icon={<TwitterX color={defaultTextColor} />}
              language={language}
              title="X"
              url="https://twitter.com/intent/tweet?url="
            />

            <ShareItem
              hoverBackgroundColor={shareLinkHoverBackgroundColor}
              icon={<Linkedin color={defaultTextColor} />}
              language={language}
              title="LinkedIn"
              url="https://www.linkedin.com/sharing/share-offsite/?url="
            />
          </MenuList>
        </Menu>
      )}
    </ShareWrapper>
  );
};

export default Share;
